import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Button, Radio, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { ManualLoader } from "src/components";
import {
  useGetRenewalEligibilityQuery,
  useGetUserQuery,
} from "src/store/services/privateApi";
import { getCurrentLease } from "src/helpers/helperLease";
import tokens from "src/shared/theme/tokens";
import {
  RenewalWizardShell,
  renewalWizardFooterSx,
} from "./RenewalWizardShell";

type Choice = "renew" | "move-out" | null;

/**
 * COPS Phase 1 choice screen inside the renewal wizard.
 */
export const RenewalChoiceScreen = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data: userData } = useGetUserQuery(null);
  const currentLease = getCurrentLease(userData);
  const leaseId = currentLease?.id ?? "";
  const [choice, setChoice] = useState<Choice>(null);

  const {
    data: eligibility,
    isLoading,
    isError,
  } = useGetRenewalEligibilityQuery(leaseId, { skip: !leaseId });

  useEffect(() => {
    if (!isLoading && eligibility && eligibility.mode !== "self_service") {
      navigate("/home", { replace: true });
    }
  }, [eligibility, isLoading, navigate]);

  if (!leaseId || isLoading) {
    return <ManualLoader />;
  }

  if (isError || eligibility?.mode !== "self_service") {
    return <ManualLoader />;
  }

  const options: {
    id: Exclude<Choice, null>;
    title: string;
    description: string;
  }[] = [
    {
      id: "renew",
      title: t("renewal.choice.renew"),
      description: t("renewal.choice.renewDescription"),
    },
    {
      id: "move-out",
      title: t("renewal.choice.moveOut"),
      description: t("renewal.choice.moveOutDescription"),
    },
  ];

  const footer = (
    <Box sx={{ ...renewalWizardFooterSx, justifyContent: "flex-end" }}>
      <Button
        variant="contained"
        color="primary"
        disabled={!choice}
        onClick={() => {
          if (choice === "renew") navigate("/renewal/renew");
          else if (choice === "move-out") navigate("/renewal/move-out");
        }}
        sx={{ minWidth: 140 }}
      >
        {t("renewal.steps.continue")}
      </Button>
    </Box>
  );

  return (
    <RenewalWizardShell path="choose" activeStep={0} footer={footer}>
      <Typography
        component="h1"
        sx={{
          fontFamily: tokens.fonts.display,
          fontWeight: 700,
          fontSize: { xs: 22, sm: 28 },
          color: tokens.colors.primaryDeep,
          mb: 1,
        }}
      >
        {t("renewal.choice.title")}
      </Typography>
      <Typography sx={{ mb: 3, color: tokens.colors.inkSecondary }}>
        {t("renewal.choice.subtitle")}
      </Typography>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
          gap: 2,
        }}
      >
        {options.map((opt) => {
          const selected = choice === opt.id;
          return (
            <Box
              key={opt.id}
              component="button"
              type="button"
              onClick={() => setChoice(opt.id)}
              sx={{
                textAlign: "left",
                cursor: "pointer",
                font: "inherit",
                color: "inherit",
                p: 2.5,
                borderRadius: `${tokens.radius.md}px`,
                border: `2px solid ${
                  selected ? tokens.colors.primary : tokens.colors.border
                }`,
                backgroundColor: selected
                  ? tokens.colors.primarySoft
                  : tokens.colors.surface,
                boxShadow: selected ? "none" : tokens.shadows.card,
                display: "flex",
                gap: 1.5,
                alignItems: "flex-start",
                transition:
                  "border-color 0.15s ease, background-color 0.15s ease",
                "&:hover": {
                  borderColor: tokens.colors.primary,
                  backgroundColor: tokens.colors.primarySoft,
                },
              }}
            >
              <Radio
                checked={selected}
                tabIndex={-1}
                disableRipple
                sx={{ p: 0, mt: 0.25 }}
              />
              <Box>
                <Typography sx={{ fontWeight: 700, mb: 0.5 }}>
                  {opt.title}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ color: tokens.colors.inkSecondary }}
                >
                  {opt.description}
                </Typography>
              </Box>
            </Box>
          );
        })}
      </Box>
    </RenewalWizardShell>
  );
};
